import { useEffect, useRef, useState, useCallback } from "react";
import { getAuthToken } from "../lib/api";
import { toast } from "sonner";

const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/^http/, "ws");

const TOAST_TYPES = {
  welcome_promo: { kind: "success", duration: 12000 },
  pack_expiration_warning: { kind: "warning", duration: 15000 },
  pack_purchase_approved: { kind: "success", duration: 10000 },
  pack_purchase_rejected: { kind: "error", duration: 8000 },
  new_tft_payment_request: { kind: "info", duration: 8000 },
  new_pack_purchase_request: { kind: "info", duration: 8000 },
  tft_recharge_approved: { kind: "success", duration: 8000 },
  new_user_registered: { kind: "info", duration: 8000 },
  new_booking: { kind: "info", duration: 10000 },
  driver_on_my_way: { kind: "success", duration: 15000, vibrate: [180, 90, 180, 90, 400] },
};

// Play a soft 2-tone chime via Web Audio API (no asset required).
let _audioCtx = null;
function _getCtx() {
  const Ctx = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!Ctx) return null;
  if (!_audioCtx) _audioCtx = new Ctx();
  if (_audioCtx.state === "suspended") _audioCtx.resume().catch(() => {});
  return _audioCtx;
}

function playForegroundChime() {
  try {
    const ctx = _getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const tone = (freq, start, dur, gain = 0.18) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(gain, now + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };
    tone(880, 0, 0.28);
    tone(1320, 0.18, 0.32);
  } catch (e) { /* noop */ }
}

// =============================================================
// Notification sound preferences (per-device, stored in localStorage)
// =============================================================
const SOUND_PREF_KEY = "trigotour_notification_sound_v1";
export const RINGTONE_OPTIONS = [
  { id: "bell",   label: "Cloche (par défaut)" },
  { id: "siren",  label: "Sirène (urgent)" },
  { id: "beep",   label: "Bip court" },
];
export function readSoundPrefs() {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(SOUND_PREF_KEY) : null;
    if (!raw) return { ringtone: "bell", volume: 0.8 };
    const v = JSON.parse(raw);
    return {
      ringtone: RINGTONE_OPTIONS.find((o) => o.id === v.ringtone) ? v.ringtone : "bell",
      volume: Math.min(1, Math.max(0, Number(v.volume ?? 0.8))),
    };
  } catch {
    return { ringtone: "bell", volume: 0.8 };
  }
}
export function writeSoundPrefs(prefs) {
  try {
    localStorage.setItem(SOUND_PREF_KEY, JSON.stringify(prefs));
  } catch { /* noop */ }
}

// Loud attention-grabbing ringtone for new bookings.
// 3 distinct profiles, all hard-capped at 8 seconds.
let _ringtoneTimer = null;
let _ringtoneStopAt = 0;

function _playRingtoneCycle(ctx, profile, volume) {
  const now = ctx.currentTime;
  const tone = (type, freq, start, dur, gain) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0, now + start);
    g.gain.linearRampToValueAtTime(gain * volume, now + start + 0.015);
    g.gain.linearRampToValueAtTime(gain * volume * 0.85, now + start + dur * 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.05);
  };
  if (profile === "siren") {
    // Sweep 700Hz → 1400Hz over 0.45s, square wave
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(700, now);
    osc.frequency.linearRampToValueAtTime(1400, now + 0.22);
    osc.frequency.linearRampToValueAtTime(700, now + 0.45);
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.45 * volume, now + 0.02);
    g.gain.linearRampToValueAtTime(0.45 * volume, now + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.55);
  } else if (profile === "beep") {
    // Three short high-pitched beeps (like an alarm clock)
    tone("square", 2000, 0,    0.08, 0.4);
    tone("square", 2000, 0.14, 0.08, 0.4);
    tone("square", 2000, 0.28, 0.08, 0.4);
  } else {
    // Default "bell": 2-tone bell (1175Hz → 880Hz), square wave
    tone("square", 1175, 0,     0.225, 0.42);
    tone("square", 880,  0.225, 0.225, 0.42);
  }
}

function playBookingRingtone(profileOverride, volumeOverride) {
  try {
    const ctx = _getCtx();
    if (!ctx) return;
    if (_ringtoneTimer && Date.now() < _ringtoneStopAt) return; // anti-stack
    const prefs = readSoundPrefs();
    const profile = profileOverride || prefs.ringtone;
    const volume = typeof volumeOverride === "number" ? volumeOverride : prefs.volume;
    const MAX_MS = 8000;
    const CYCLE_MS = profile === "beep" ? 700 : 1000;
    _ringtoneStopAt = Date.now() + MAX_MS;

    const playCycle = () => {
      if (Date.now() >= _ringtoneStopAt) {
        clearInterval(_ringtoneTimer);
        _ringtoneTimer = null;
        return;
      }
      _playRingtoneCycle(ctx, profile, volume);
    };
    playCycle();
    _ringtoneTimer = setInterval(playCycle, CYCLE_MS);
  } catch (e) { /* noop */ }
}

// One-shot preview (single cycle, no timer)
function previewRingtone(profile, volume = 0.8) {
  try {
    const ctx = _getCtx();
    if (!ctx) return;
    _playRingtoneCycle(ctx, profile, volume);
  } catch (e) { /* noop */ }
}

function stopBookingRingtone() {
  if (_ringtoneTimer) {
    clearInterval(_ringtoneTimer);
    _ringtoneTimer = null;
  }
  _ringtoneStopAt = 0;
}

// Exposed so the SW postMessage handler in usePushNotifications can reuse the exact same audio.
if (typeof window !== "undefined") {
  window.__trigoPlayBookingRingtone = playBookingRingtone;
  window.__trigoPlayChime = playForegroundChime;
  window.__trigoStopBookingRingtone = stopBookingRingtone;
  window.__trigoPreviewRingtone = previewRingtone;
}

export function useWebSocketNotifications(userId) {
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const connect = useCallback(() => {
    const token = getAuthToken();
    if (!token || !WS_BASE || !userId) return;

    const ws = new WebSocket(`${WS_BASE}/api/ws/notifications/${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Broadcast to any listener that wants to react to specific message types
        try { window.dispatchEvent(new CustomEvent("trigo:ws-message", { detail: data })); } catch { /* noop */ }
        setNotifications(prev => [{ ...data, id: Date.now().toString(), read: false, time: new Date().toISOString() }, ...prev].slice(0, 30));
        // Fire a toast for high-impact event types
        const cfg = TOAST_TYPES[data?.type];
        if (cfg && (data?.message || data?.title)) {
          // Loud ringtone for new bookings, short chime for everything else.
          if (data?.type === "new_booking") {
            // Register the alert so the loop keeps re-playing the ringtone
            // every 10s until the driver accepts/refuses (or hits "Arrêter").
            if (typeof window !== "undefined" && typeof window.__trigoRegisterBookingAlert === "function") {
              window.__trigoRegisterBookingAlert(
                data.booking_id || `tmp-${Date.now()}`,
                data.message || ""
              );
            } else {
              playBookingRingtone();
            }
          } else {
            playForegroundChime();
          }
          // Trigger device vibration for high-visibility notifications.
          if (cfg.vibrate && typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
            try { navigator.vibrate(cfg.vibrate); } catch { /* noop */ }
          }
          const fn = toast[cfg.kind] || toast;
          const isExpress = data?.booking_type === "express";
          const title = data.title || (data?.type === "new_booking"
            ? (isExpress ? "Nouvelle course Express" : "Nouvelle réservation")
            : "Notification TrigoTour");
          fn(title, {
            description: data.message || "",
            duration: cfg.duration,
            action: data.deeplink ? {
              label: "Voir",
              onClick: () => {
                stopBookingRingtone();
                try { window.location.href = data.deeplink; } catch { /* noop */ }
              },
            } : {
              label: "OK",
              onClick: () => stopBookingRingtone(),
            },
          });
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.warn("WS parse error:", e);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      reconnectRef.current = setTimeout(connect, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [userId]);

  useEffect(() => {
    connect();
    // Reconnect immediately when the tab comes back from background — the
    // OS may have killed our socket while it was hidden. `useAppLifecycle`
    // dispatches this event when the tab is visible again after > 5 s.
    const onResume = () => {
      try {
        if (wsRef.current) {
          const s = wsRef.current.readyState;
          if (s === WebSocket.OPEN || s === WebSocket.CONNECTING) return;
        }
      } catch { /* noop */ }
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
      connect();
    };
    window.addEventListener("trigo:app-resumed", onResume);
    return () => {
      window.removeEventListener("trigo:app-resumed", onResume);
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [connect]);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const dismiss = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return { notifications, unreadCount, connected, markAllRead, dismiss };
}
